import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
    Box,
    Typography,
    Theme,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import NoDataText from '../../components/NoDataText';
import OnboardingStatusLegend from '../../components/OnboardingStatusLegend';
import TableContainer from '../../components/Table/TableContainer';
import { getFirmClientsListTableLabels } from '../../constants/tableLabels';
import usePagination from '../../hooks/usePagination';
import { ActiveSortField, ClientData, FilterTypes } from '../../types';
import { handleSortTableData } from '../../helpers/handleSortTableData';
import { makeStyles } from 'tss-react/mui';
import ClientsTableDataRow from '../ClientsView/ClientsTableDataRow';
import { getClientsList } from '../../api/clientsApi/clientsApi';
import FilterCards from '../../components/FilterCards';
import { TRACK } from '../../analytics/catalog';
import theme from '../../theme';
import TableActions from '../../components/Table/TableActions';
import Loader from '../../components/Loader';


const useStyles = makeStyles()((theme: Theme) => ({
    tableLabel: {
        fontSize: '20px',
        fontWeight: 600,
        lineHeight: '120%',
        color: theme.palette.common.white,
        fontFamily: 'Poppins',
    },
    tableBox: {
        flex: 1,
        minWidth: 0,
        paddingBottom: theme.spacing(2),
    },
    accordion: {
        backgroundColor: 'transparent',
        marginTop: theme.spacing(1),
        boxShadow: 'none',
        overflow: 'hidden',
        fontFamily: 'Poppins',
        borderRadius: '8px',
    },
    accordionSummary: {
        backgroundColor: theme.palette.primary.light,
        minHeight: '56px',
        padding: '0 16px',
        fontFamily: 'Poppins',
        '& .MuiTypography-root': {
            fontWeight: 600,
            fontSize: '16px',
            color: theme.palette.common.white,
            fontFamily: 'Poppins',
        },
        flexDirection: 'row-reverse',
    },
    accordionDetails: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.primary.light,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
        fontFamily: 'Poppins',
    },
    tableContainer: {
        width: '99%',
        background: 'transparent',
        border: `1px solid ${theme.palette.secondary.dark}`,
        borderRadius: '8px',
        overflow: 'auto',
    },
}));

const ClientsTable = () => {
    const { classes } = useStyles();
    const params = useParams();
    const firmId = localStorage.getItem('firmId');

    const {
        pageNumber,
        rowsPerPage,
        setTotalCount,
        setPageNumber,
    } = usePagination();

    const [clientsList, setClientsList] = useState<ClientData[]>([]);
    const [blockedCount, setBlockedCount] = useState(0);


    const [isDataLoading, setIsDataLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState<string>();
    const [isAccordionOpen, setIsAccordionOpen] = useState(false);
    const [match, setMatch] = useState<string>();

    const hasParams = !!Object.entries(params).length;

    const [filters, setFilters] = useState<FilterTypes>({
        firmId: firmId || '',
        clientId: '',
        defendantId: '',
        onboardingStatus: '',
    });

    const [activeSortField, setActiveSortField] =
        useState<ActiveSortField | null>(null);

    const sortableFields = {
        name: {
            order: '',
            fieldName: 'name',
        },
        updatedAt: {
            order: '',
            fieldName: 'updatedAt',
        },
    };

    const clientFilters = [
        {
            status: 'Onboarding Blocked',
            count: blockedCount,
            color: theme.palette.error.main,
            trackId: TRACK.dashboard.clients.onboardingBlocked,
        },
    ];

    const handleFilterChange = (filter?: string) => {
        setIsAccordionOpen(true);
        setIsDataLoading(true);
        setClientsList([]);
        setPageNumber?.(1);

        setActiveFilter(filter);

        setFilters((prev) => ({
            ...prev,
            onboardingStatus:
                filter === 'Onboarding Blocked' ? 'BLOCKED' : '',
        }));
    };

    useEffect(() => {
        setIsDataLoading(true);

        getClientsList({
            page: pageNumber,
            size: rowsPerPage,
            count: pageNumber === 1,
            match,
            filters: { ...filters, ...(hasParams && params) },
            activeSortField,
        })
            .then((res) => {
                if (res.data) {
                    if (pageNumber === 1) {
                        setTotalCount?.(res.data.totalCount || 0);
                    }

                    setBlockedCount(res.data.blockedCount || 0);
                    setClientsList(res.data.resultSet || []);
                }
            })
            .finally(() => {
                setIsDataLoading(false);
            });
    }, [
        pageNumber,
        rowsPerPage,
        filters,
        match,
        activeSortField,
        hasParams,
        params,
        setTotalCount,
    ]);

    return (
        <Box sx={{ marginBottom: '4px' }}>
            <Typography
                className={classes.tableLabel}
                sx={{ marginBottom: '12px' }}
            >
                Client Onboarding
            </Typography>

            <FilterCards
                filters={clientFilters}
                activeFilter={activeFilter}
                onFilterChange={handleFilterChange}
            />

            <Accordion
                className={classes.accordion}
                sx={{ marginBottom: '14px' }}
                expanded={isAccordionOpen}
                onChange={(_, expanded) => setIsAccordionOpen(expanded)}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                    className={classes.accordionSummary}
                >
                    <Typography className={classes.tableLabel}>
                        Clients
                    </Typography>
                </AccordionSummary>

                <AccordionDetails className={classes.accordionDetails}>
                    <Box className={classes.tableBox}>
                        <TableActions
                            hasParams={hasParams}
                            match={match}
                            setMatch={(value) => {
                                // Loading is set by the fetch effect. Setting it here would leave the
                                // loader stuck when TableActions repeats setMatch('') with no refetch.
                                setMatch(value);
                                setPageNumber?.(1);
                            }}
                        />

                        {isDataLoading ? (
                            <Box position="absolute" top="50%" left="50%">
                                <Loader colorType="warning" size={60} />
                            </Box>
                        ) : clientsList.length === 0 ? (
                            <NoDataText marginTop="20px" />
                        ) : (
                            <>
                                {localStorage.getItem('showUserOnboardingDetails') === 'true' && (
                                    <Box sx={{ marginBottom: '12px' }}>
                                        <OnboardingStatusLegend />
                                    </Box>
                                )}
                                <TableContainer
                                    headerLabels={getFirmClientsListTableLabels(
                                        localStorage.getItem(
                                            'showUserOnboardingDetails'
                                        ) === 'true'
                                    )}
                                    sortableFields={sortableFields}
                                    activeSortField={activeSortField}
                                    setActiveSortField={setActiveSortField}
                                    handleSortTableData={handleSortTableData}
                                    dashboard
                                >
                                    <ClientsTableDataRow tableData={clientsList} />
                                </TableContainer>
                            </>
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default ClientsTable;